import "vue-multiselect/dist/vue-multiselect.min.css";
import { AxiosError } from "axios";
import Multiselect from "vue-multiselect";
import { Vue, Component, Prop } from "vue-property-decorator";
import { IClientAppOption, TAppVersionFlagForm } from "../types";
import capitalizeFirstLetter from "@/libs/capitalizeFirstLetter";

@Component({
  components: {
    Multiselect,
  },
})
export default class AppVersionFlagForm extends Vue {
  @Prop({ type: Object, required: true })
  private readonly formData!: TAppVersionFlagForm;
  @Prop({ type: Boolean, default: false })
  private readonly isLoadingClientApps!: boolean;
  @Prop({ type: Map, required: true }) private readonly clientApps!: Map<
    number,
    IClientAppOption
  >;

  private fieldErrors: Record<string, string> = {};
  private isLoading = false;

  private get isCreateMode() {
    return !this.formData.id;
  }

  private get clientAppsOptions() {
    const items: IClientAppOption[] = [];

    for (const item of this.clientApps.values()) {
      items.push(item);
    }

    return items;
  }

  private get multiselectClientApp() {
    const clientAppId = this.formData.clientAppId;

    return this.clientApps.get(clientAppId);
  }

  private set multiselectClientApp(item) {
    this.formData.clientAppId = item ? item.value : 0;
  }

  private onSubmitForm() {
    if (this.isCreateMode) {
      this.createAppVersion();
    } else {
      this.updateAppVersion();
    }
  }

  private onResetForm() {
    this.$emit("cancel");
  }

  private clearFieldError(name: string) {
    this.$delete(this.fieldErrors, name);
  }

  private saveFieldErrors(error: AxiosError) {
    const violations = (error.response &&
      error.response.data &&
      error.response.data.violations) as
      | {
          propertyPath: string;
          title: string;
        }[]
      | undefined;

    if (!Array.isArray(violations)) {
      return;
    }

    this.fieldErrors = violations.reduce<Record<string, string>>(
      (acc, { propertyPath, title }) => {
        if (["major", "minor", "patch"].includes(propertyPath)) {
          acc.semver = `${capitalizeFirstLetter(propertyPath)}: ${title}`;
        } else {
          acc[propertyPath] = title;
        }

        return acc;
      },
      {}
    );
  }

  private async createAppVersion() {
    if (this.isLoading) {
      return;
    }

    const appName =
      this.multiselectClientApp && this.multiselectClientApp.label;

    this.isLoading = true;

    const payload = {
      clientAppId: this.formData.clientAppId,
      major: +this.formData.major,
      minor: +this.formData.minor,
      patch: +this.formData.patch,
      di: this.formData.di,
    };

    try {
      const { createAppVersionFlag } = await import(
        "@/api/appVersionFlag/createAppVersionFlag"
      );

      await createAppVersionFlag(payload);

      this.$emit("success");

      this.$notify({
        group: "notice",
        title: "App version",
        text: `Version ${payload.major}.${payload.minor}.${payload.patch} is created for ${appName}`,
        type: "success",
      });
    } catch (e) {
      const error = e as AxiosError;

      this.saveFieldErrors(error);
    } finally {
      this.isLoading = false;
    }
  }

  private async updateAppVersion() {
    if (this.isLoading) {
      return;
    }

    const appName =
      this.multiselectClientApp && this.multiselectClientApp.label;

    this.isLoading = true;

    const payload = {
      id: this.formData.id,
      clientAppId: this.formData.clientAppId,
      major: +this.formData.major,
      minor: +this.formData.minor,
      patch: +this.formData.patch,
      di: this.formData.di,
    };

    try {
      const { updateAppVersionFlag } = await import(
        "@/api/appVersionFlag/updateAppVersionFlag"
      );

      await updateAppVersionFlag(payload);

      this.$emit("success");

      this.$notify({
        group: "notice",
        title: "App version",
        text: `Version ${payload.major}.${payload.minor}.${payload.patch} is updated for ${appName}`,
        type: "success",
      });
    } catch (e) {
      const error = e as AxiosError;

      this.saveFieldErrors(error);
    } finally {
      this.isLoading = false;
    }
  }
}
